import { createSlice } from '@reduxjs/toolkit';

import { fetchGet, fetchPost } from '../../helpers/fetching';
import { formatPatientName } from '../../helpers/misc';
import { setSnackbar, fetchPatientHistory } from '../index';

const initialState = {
  exam_id: '',
  folder: '',
  pat_name: '',
  patient_id: '',
  birth: '0000-00-00T00:00:00.000Z',
  referring: '',
  device: '',
  operator: '',
  notes: '',

  life_time_risk: null,

  applied_density: 'bbox',
  applied_quality: 'bbox',
  applied_opacities: 'bbox',
  applied_microcalc: 'bbox',

  confirmed_density: null,
  confirmed_quality: null,
  confirmed_opacities: null,
  confirmed_microcalc: null,

  shown_density: 'bbox',
  shown_quality: 'bbox',
  shown_opacities: 'bbox',
  shown_microcalc: 'bbox',

  projections: {
    rcc: null,
    lcc: null,
    rmlo: null,
    lmlo: null,
  },

  evaluations: {},

  second_captures: {
    rcc: [],
    lcc: [],
    rmlo: [],
    lmlo: [],
  },

  priors_found: false,
};

export const resultsSlice = createSlice({
  name: 'results',
  initialState,
  reducers: {
    setResults: (_, action) => {
      return { ...action.payload };
    },
    setInitialResult: () => {
      return { ...initialState };
    },
    setOperatorNotes: (state, action) => {
      state.notes = action.payload;
    },
    setShownDensity: (state, action) => {
      state.shown_density = action.payload;
    },
    setShownQuality: (state, action) => {
      state.shown_quality = action.payload;
    },
    setShownMicrocalc: (state, action) => {
      state.shown_microcalc = action.payload;
    },
    setShownOpacities: (state, action) => {
      state.shown_opacities = action.payload;
    },
  },
});

export const {
  setResults,
  setInitialResult,
  setOperatorNotes,
  setShownDensity,
  setShownQuality,
  setShownMicrocalc,
  setShownOpacities,
} = resultsSlice.actions;

export default resultsSlice.reducer;

// Derived
export const setConfirmed = (type, confirmed_by) => {
  return async (dispatch, getState) => {
    const key = `confirmed_${type}`;
    const currentState = { ...getState().results };

    if (!currentState.hasOwnProperty(key)) {
      console.log(`Bad result type: ${type}`);
      return;
    }

    currentState[key] = confirmed_by;
    dispatch(setResults(currentState));
  };
};

export const setApplied = (type, created_by) => {
  return async (dispatch, getState) => {
    const key = `applied_${type}`;
    const currentState = { ...getState().results };

    if (!currentState.hasOwnProperty(key)) {
      console.log(`Bad result type: ${type}`);
      return;
    }

    currentState[key] = created_by;
    dispatch(setResults(currentState));
  };
};

// Async
export const fetchResults = () => {
  return async (dispatch, getState) => {
    const currentExam = getState().examinations.currentExam;

    if (!currentExam) return;

    const response = await fetchGet(`examination/${currentExam}`);

    if (!response.success) {
      dispatch(
        setSnackbar({
          msg: `api_messages.${response.msg}`,
          severity: 'error',
        })
      );
      dispatch(setResults({ ...initialState, error: response.msg }));
      return;
    }
    let resultData = response.data;
    if ('pat_name' in resultData) {
      resultData.pat_name = formatPatientName(resultData.pat_name);
    }

    dispatch(fetchPatientHistory(resultData.patient_id));
    dispatch(setResults(resultData));
  };
};

export const postOperatorNotes = (note, t) => {
  return async (dispatch, getState) => {
    const exam_id = getState().examinations.currentExam;

    const response = await fetchPost('operator_notes', { exam_id, note });
    if (response.success) {
      dispatch(setOperatorNotes(note));
      dispatch(
        setSnackbar({
          msg: 'Limitations.limitations_update',
          severity: 'success',
        })
      );
    } else {
      dispatch(
        setSnackbar({
          msg: `api_messages.${response.msg}`,
          severity: 'error',
        })
      );
    }
  };
};

// Send each report type
export const sendQualityReport = (currentExam, loggedUser, t) => {
  console.log('send Quality Report', { currentExam, loggedUser });
  return async (dispatch) => {
    const responseQuality = await fetchPost(
      `report/${currentExam}?report_type=quality`,
      {
        username: loggedUser,
      },
      'bbox'
    );
    if (responseQuality.success) {
      dispatch(
        setSnackbar({
          msg: responseQuality.success ? t('report.report_sent') : responseQuality.Message,
          severity: responseQuality.success ? 'success' : 'warning',
        })
      );
    } else {
      dispatch(
        setSnackbar({
          msg: responseQuality.msg ? responseQuality.msg : t('report.error_quality'),
          severity: 'error',
        })
      );
    }
  };
};

export const sendDensityReport = (currentExam, loggedUser, t) => {
  return async (dispatch) => {
    const responseDensity = await fetchPost(
      `report/${currentExam}?report_type=density`,
      {
        username: loggedUser,
      },
      'bbox'
    );
    if (responseDensity.success) {
      dispatch(
        setSnackbar({
          msg: t('report.report_sent'),
          severity: 'success',
        })
      );
    } else {
      dispatch(
        setSnackbar({
          msg: responseDensity.msg ? responseDensity.msg : t('report.error_density'),
          severity: 'error',
        })
      );
    }
  };
};

export const sendDiagnosticsReport = (currentExam, loggedUser, t) => {
  console.log('send Diagnostics Report', { currentExam, loggedUser });
  return async (dispatch) => {
    const responseDiagnostics = await fetchPost(
      `report/${currentExam}?report_type=diagnostics`,
      {
        username: loggedUser,
      },
      'bbox'
    );
    if (responseDiagnostics.success) {
      dispatch(
        setSnackbar({
          msg: responseDiagnostics.success ? t('report.report_sent') : responseDiagnostics.Message,
          severity: responseDiagnostics.success ? 'success' : 'warning',
        })
      );
    } else {
      dispatch(
        setSnackbar({
          msg: responseDiagnostics.msg ? responseDiagnostics.msg : t('report.error_diagnostics'),
          severity: 'error',
        })
      );
    }
  };
};

export const sendStructuredReport = (currentExam, loggedUser, t) => {
  console.log('send Structured Report', { currentExam, loggedUser });
  return async (dispatch) => {
    const response = await fetchPost(
      `report/${currentExam}?report_type=sr`,
      {
        username: loggedUser,
      },
      'bbox'
    );
    if (response.success) {
      dispatch(
        setSnackbar({
          msg: response.success ? t('report.structured_sent') : response.Message,
          severity: response.success ? 'success' : 'warning',
        })
      );
    } else {
      dispatch(
        setSnackbar({
          msg: response.msg ? `api_messages.${response.msg}` : t('report.error_reports'),
          severity: 'error',
        })
      );
    }
  };
};

export const sendScoreCardReport = (currentExam, loggedUser, t) => {
  console.log('send ScoreCard Report', { currentExam, loggedUser });
  return async (dispatch) => {
    const response = await fetchPost(
      `report/${currentExam}?report_type=score_card`,
      {
        username: loggedUser,
      },
      'bbox'
    );
    if (response.success) {
      dispatch(
        setSnackbar({
          msg: response.success ? t('report.score_card_sent') : response.Message,
          severity: response.success ? 'success' : 'warning',
        })
      );
    } else {
      dispatch(
        setSnackbar({
          msg: response.msg ? `api_messages.${response.msg}` : t('report.error_reports'),
          severity: 'error',
        })
      );
    }
  };
};

export const sendFHIRReport = (currentExam, loggedUser, t) => {
  console.log('send FHIR Report', { currentExam, loggedUser });
  return async (dispatch) => {
    const response = await fetchPost(
      `fhir/${currentExam}`,
      {
        username: loggedUser,
      },
      'bbox'
    );
    if (response.success) {
      dispatch(
        setSnackbar({
          msg: response.success ? t('report.fhir_sent') : response.Message,
          severity: response.success ? 'success' : 'warning',
        })
      );
    } else {
      dispatch(
        setSnackbar({
          msg: response.msg ? `api_messages.${response.msg}` : t('report.error_reports'),
          severity: 'error',
        })
      );
    }
  };
};

export const sendHL7Report = (currentExam, loggedUser, t) => {
  console.log('send HL7 Report', { currentExam, loggedUser });
  return async (dispatch) => {
    const response = await fetchPost(
      `hl7/${currentExam}`,
      {
        username: loggedUser,
      },
      'bbox'
    );
    if (response.success) {
      dispatch(
        setSnackbar({
          msg: response.success ? t('report.hl7_sent') : response.Message,
          severity: response.success ? 'success' : 'warning',
        })
      );
    } else {
      dispatch(
        setSnackbar({
          msg: response.msg ? `api_messages.${response.msg}` : t('report.error_reports'),
          severity: 'error',
        })
      );
    }
  };
};

// Send automatic reports handler
export const sendManualReports = (reportModule, currentExam, loggedUser, t, confirmAction = false) => {
  return async (dispatch, getState) => {
    const { report_sending_rules } = getState().results;
    const { quality_module, density_module } = getState().global_settings.premium_features;
    const {
      diagnostics_report_manual,
      quality_report_manual,
      density_report_manual,
      score_card_manual,
      structured_report_manual,
    } = report_sending_rules;
    const { fhir_sending_rules, hl7_sending_rules } = getState().results;
    const { fhir_manual } = fhir_sending_rules || {};
    const { hl7_manual } = hl7_sending_rules || {};

    if (reportModule === 'quality' && quality_module && quality_report_manual) {
      await dispatch(sendQualityReport(currentExam, loggedUser, t));
    } else if (reportModule === 'density' && density_module && density_report_manual) {
      await dispatch(sendDensityReport(currentExam, loggedUser, t));
    } else if (
      (reportModule === 'diagnostics' || reportModule === 'opacities' || reportModule === 'microcalc') &&
      diagnostics_report_manual
    ) {
      await dispatch(sendDiagnosticsReport(currentExam, loggedUser, t));
    }
    if (reportModule === 'structured' || (structured_report_manual && !confirmAction)) {
      dispatch(sendStructuredReport(currentExam, loggedUser, t));
    }
    if (reportModule === 'score_card' || score_card_manual) {
      dispatch(sendScoreCardReport(currentExam, loggedUser, t));
    }
    if (reportModule === 'fhir' || fhir_manual) {
      dispatch(sendFHIRReport(currentExam, loggedUser, t));
    }
    if (reportModule === 'hl7' || hl7_manual) {
      dispatch(sendHL7Report(currentExam, loggedUser, t));
    }
  };
};
